// Client half of the admin console TOTP two-factor flow (functions/adminMfa.js).
import { httpsCallable } from 'firebase/functions';
import { auth, functions } from '@/lib/firebase';

/**
 * Device trust token. Only proves "this browser already passed a code once", so
 * it is useless without the account password — but it is still per-browser and
 * never leaves this origin.
 */
const DEVICE_TOKEN_KEY = 'ysr_admin_mfa_device';

export const getDeviceToken = () => {
  try {
    return localStorage.getItem(DEVICE_TOKEN_KEY) || null;
  } catch {
    return null;
  }
};

const setDeviceToken = (token) => {
  try {
    if (token) localStorage.setItem(DEVICE_TOKEN_KEY, token);
  } catch {
    /* private browsing — the user just gets asked for a code next time */
  }
};

export const clearDeviceToken = () => {
  try {
    localStorage.removeItem(DEVICE_TOKEN_KEY);
  } catch {
    /* nothing we can do, and nothing depends on it */
  }
};

const call = (name, payload = {}) => httpsCallable(functions, name)(payload).then((r) => r?.data);

/**
 * Claims are baked into the ID token, so anything the server just changed is
 * invisible until the token is refreshed.
 */
const refreshIdToken = async () => {
  if (auth?.currentUser) {
    await auth.currentUser.getIdToken(true);
  }
};

export const fetchMfaStatus = async () => {
  const data = await call('adminMfaStatus', { deviceToken: getDeviceToken() });
  if (data?.claimRefreshed) {
    await refreshIdToken();
  }
  return data;
};

export const beginMfaEnrollment = () => call('adminMfaBeginEnrollment');

export const confirmMfaEnrollment = async (code) => {
  const data = await call('adminMfaConfirmEnrollment', { code });
  await refreshIdToken();
  return data;
};

export const verifyMfaCode = async (code, rememberDevice) => {
  const data = await call('adminMfaVerify', { code, rememberDevice });
  if (data?.deviceToken) {
    setDeviceToken(data.deviceToken);
  }
  await refreshIdToken();
  return data;
};

export const revokeMfaDevices = async () => {
  const data = await call('adminMfaRevokeDevices');
  clearDeviceToken();
  await refreshIdToken();
  return data;
};

/** Turns a Cloud Functions error into something worth showing a human. */
export const mfaErrorMessage = (error) => {
  const code = error?.code || '';
  if (code.includes('resource-exhausted')) {
    return 'Too many incorrect codes. Wait a few minutes and try again.';
  }
  if (code.includes('permission-denied')) {
    return 'This account does not have admin access.';
  }
  return error?.message || 'Could not verify that code. Please try again.';
};
